/**
 * Owner: Masa Bayu
 * File: demo-library-sync.ts
 * Date:Time: 2026-09-16 06:52
 * Description: T4-E3 demo sync/continuity orchestration (queue, conflict, recovery).
 */

import {
  createEmptyLibrarySyncQueue,
  createLibraryContinuitySnapshot,
  createQxreadTelemetryEvent,
  enqueueLibrarySyncChange,
  markLibraryItemOfflineStale,
  markLibrarySyncChangeInFlight,
  ackLibrarySyncChange,
  failLibrarySyncChange,
  listRetryableLibrarySyncChanges,
  reconcileLibraryContinuity,
  recoverInterruptedLibrarySync,
  beginLibrarySync,
  completeLibrarySync,
  type QxreadLibraryContinuitySnapshot,
  type QxreadLibraryItem,
  type QxreadLibrarySyncChange,
  type QxreadLibrarySyncConflict,
  type QxreadLibrarySyncContract,
  type QxreadLibrarySyncQueue,
  type QxreadTelemetryEvent,
} from '@qx/qxread-core';
import {
  applyDemoLibraryItemUpdate,
  getDemoLibraryProjection,
  getDemoLibrarySync,
  setDemoLibrarySync,
} from './demo-library-store';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_reader';
const LOCAL_DEVICE = 'qxrddev_demo_local';
const REMOTE_DEVICE = 'qxrddev_demo_remote';

let queue: QxreadLibrarySyncQueue = createEmptyLibrarySyncQueue(DEMO_ACCOUNT_ID);
let remoteByItemId = new Map<string, QxreadLibraryContinuitySnapshot>();
let lastConflict: QxreadLibrarySyncConflict | null = null;
let lastTelemetry: QxreadTelemetryEvent | null = null;

function record(
  name:
    | 'qxread.library.continuity'
    | 'qxread.library.sync_enqueue'
    | 'qxread.library.sync_reconcile'
    | 'qxread.library.sync_recovery'
    | 'qxread.library.sync_state',
  attributes: Readonly<Record<string, string | number | boolean | null>>,
): void {
  lastTelemetry = createQxreadTelemetryEvent({
    name,
    correlationId: DEMO_ACCOUNT_ID,
    attributes,
  });
}

function findOwnedItem(publicationId: string): QxreadLibraryItem {
  getDemoLibraryProjection();
  const projection = getDemoLibraryProjection();
  const pool = [
    ...projection.purchased,
    ...projection.continueReading,
    ...projection.completed,
    ...projection.blocked,
    ...projection.archived,
  ];
  const item = pool.find((entry) => entry.publicationId === publicationId);
  if (item === undefined) {
    throw new Error('Demo sync library item not found');
  }
  return item;
}

export function queueDemoLibraryProgress(publicationId: string): {
  readonly change: QxreadLibrarySyncChange;
  readonly duplicated: boolean;
} {
  const item = findOwnedItem(publicationId);
  const snapshot = createLibraryContinuitySnapshot(item, {
    deviceId: LOCAL_DEVICE,
    revision: Math.max(1, Math.floor(item.progress.percentComplete)),
  });
  const idempotencyKey = `idem_${item.libraryItemId}_${String(snapshot.revision)}_${snapshot.lastPositionRef ?? 'none'}`;
  const result = enqueueLibrarySyncChange(queue, {
    idempotencyKey,
    direction: 'local_to_remote',
    kind: 'progress',
    snapshot,
  });
  queue = result.queue;
  record('qxread.library.sync_enqueue', {
    publicationId,
    duplicated: result.duplicated,
    status: result.change.status,
  });
  record('qxread.library.continuity', {
    publicationId,
    percentComplete: snapshot.percentComplete,
    lastPositionRef: snapshot.lastPositionRef,
  });
  return Object.freeze({ change: result.change, duplicated: result.duplicated });
}

export function runDemoLibrarySyncPass(): {
  readonly contract: QxreadLibrarySyncContract;
  readonly acked: number;
  readonly failed: number;
} {
  let contract = beginLibrarySync(getDemoLibrarySync());
  setDemoLibrarySync(contract);
  let acked = 0;
  let failed = 0;
  for (const change of listRetryableLibrarySyncChanges(queue)) {
    queue = markLibrarySyncChangeInFlight(queue, change.changeId);
    try {
      remoteByItemId.set(change.libraryItemId, change.snapshot);
      queue = ackLibrarySyncChange(queue, change.changeId);
      acked += 1;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'sync failed';
      queue = failLibrarySyncChange(queue, change.changeId, message);
      failed += 1;
    }
  }
  contract =
    failed > 0
      ? recoverInterruptedLibrarySync(contract, 'Sync pass incomplete')
      : completeLibrarySync(contract);
  setDemoLibrarySync(contract);
  record('qxread.library.sync_state', {
    state: contract.state,
    acked,
    failed,
  });
  return Object.freeze({ contract, acked, failed });
}

export function simulateDemoRemoteContinuity(
  publicationId: string,
  options?: {
    readonly percentComplete?: number;
    readonly lastPositionRef?: string;
    readonly lastActivityAt?: string;
  },
): QxreadLibraryContinuitySnapshot {
  const item = findOwnedItem(publicationId);
  const percent = options?.percentComplete ?? Math.min(100, item.progress.percentComplete + 30);
  // Always ahead of wall-clock local lastOpenedAt so demo reconcile → remote_wins.
  const remoteActivityAt =
    options?.lastActivityAt ?? new Date(Date.now() + 60 * 60 * 1000).toISOString();
  const remoteItem = Object.freeze({
    ...item,
    progress: Object.freeze({
      ...item.progress,
      state: percent >= 100 ? ('completed' as const) : ('in_progress' as const),
      percentComplete: percent,
      lastOpenedAt: remoteActivityAt,
      lastPositionRef: options?.lastPositionRef ?? `pos_remote_${String(percent)}`,
    }),
  });
  const remote = createLibraryContinuitySnapshot(remoteItem, {
    deviceId: REMOTE_DEVICE,
    revision: Math.max(1, Math.floor(percent)),
    lastActivityAt: remoteActivityAt,
  });
  remoteByItemId.set(item.libraryItemId, remote);
  record('qxread.library.continuity', {
    publicationId,
    deviceId: REMOTE_DEVICE,
    percentComplete: remote.percentComplete,
  });
  return remote;
}

export function reconcileDemoLibraryItem(publicationId: string): {
  readonly item: QxreadLibraryItem;
  readonly conflict: QxreadLibrarySyncConflict | null;
  readonly appliedDirection: string;
} {
  const item = findOwnedItem(publicationId);
  const local = createLibraryContinuitySnapshot(item, {
    deviceId: LOCAL_DEVICE,
    revision: Math.max(1, Math.floor(item.progress.percentComplete)),
  });
  let remote = remoteByItemId.get(item.libraryItemId);
  if (remote === undefined) {
    remote = local;
  }
  const result = reconcileLibraryContinuity({
    item,
    local,
    remote,
    contract: getDemoLibrarySync(),
  });
  applyDemoLibraryItemUpdate(result.item);
  setDemoLibrarySync(result.contract);
  lastConflict = result.conflict;
  record('qxread.library.sync_reconcile', {
    publicationId,
    appliedDirection: result.appliedDirection,
    hasConflict: result.conflict !== null,
    resolution: result.conflict?.resolution ?? 'none',
  });
  return Object.freeze({
    item: result.item,
    conflict: result.conflict,
    appliedDirection: result.appliedDirection,
  });
}

export function interruptDemoLibrarySync(): QxreadLibrarySyncContract {
  const contract = recoverInterruptedLibrarySync(
    beginLibrarySync(getDemoLibrarySync()),
    'Simulated interrupted sync',
  );
  setDemoLibrarySync(contract);
  record('qxread.library.sync_recovery', {
    state: contract.state,
    hint: contract.recoveryHint,
  });
  return contract;
}

export function markDemoLibraryOfflineStale(publicationId: string): QxreadLibraryItem {
  const item = findOwnedItem(publicationId);
  const ready =
    item.state === 'offline_ready' || item.offlineAvailable
      ? item
      : Object.freeze({
          ...item,
          state: 'offline_ready' as const,
          offlineAvailable: true,
        });
  const stale = markLibraryItemOfflineStale(ready);
  applyDemoLibraryItemUpdate(stale);
  return stale;
}

export function getDemoLibrarySyncQueue(): QxreadLibrarySyncQueue {
  return queue;
}

export function getDemoLibrarySyncConflict(): QxreadLibrarySyncConflict | null {
  return lastConflict;
}

export function getDemoLibrarySyncTelemetry(): QxreadTelemetryEvent | null {
  return lastTelemetry;
}

export function resetDemoLibrarySync(): void {
  queue = createEmptyLibrarySyncQueue(DEMO_ACCOUNT_ID);
  remoteByItemId = new Map();
  lastConflict = null;
  lastTelemetry = null;
}
